/** Side-by-side player positions across tracking variants (port of
 *  qa/tracking_compare.html, same address /qa/tracking-compare/<code>).
 *
 * Each pane draws /api/v1/workbench/frames for one ?version=; a shared
 * scrubber drives the frame index. Playback advances ONLY the active
 * pane (the others freeze where playback started) so the network stays
 * calm enough to play one variant at real time. Batch prefetch with a
 * one-in-flight cap per variant, exactly the Jinja page's strategy.
 *
 * Game facts (fps, frames, org) come from /api/v1/qa/games — the same
 * list the QA index reads — instead of server-rendered context.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { API_BASE, api } from '../../lib/api'
import { pathSeg } from '../../lib/urls'
import './qa.css'

const PITCH_W_CM = 10500
const PITCH_H_CM = 6800
const PREFETCH_RADIUS = 120
const BATCH_SIZE = 120
const REFETCH_TRIGGER = 60
// The Jinja page seeded here — a mid-match frame shows a real shape where
// frame 0 is 22 statues; clamped to short games.
const SEED_FRAME = 78115

type Player = { x: number; y: number; team: number; jersey?: string | number; speed?: number }
type FrameRow = { frame: number; players?: Player[]; ball?: { x: number; y: number } }
type GameFacts = { total_frames: number; fps: number; org_id: number; org_code: string }

function drawField(ctx: CanvasRenderingContext2D, w: number, h: number) {
  ctx.fillStyle = '#1a5d2a'
  ctx.fillRect(0, 0, w, h)
  ctx.strokeStyle = 'rgba(255,255,255,0.6)'
  ctx.lineWidth = 1
  ctx.strokeRect(2, 2, w - 4, h - 4)
  ctx.beginPath(); ctx.moveTo(w / 2, 2); ctx.lineTo(w / 2, h - 2); ctx.stroke()
  ctx.beginPath(); ctx.arc(w / 2, h / 2, h * 0.12, 0, Math.PI * 2); ctx.stroke()
  const boxW = w * 0.157
  const boxH = h * 0.593
  ctx.strokeRect(2, (h - boxH) / 2, boxW, boxH)
  ctx.strokeRect(w - boxW - 2, (h - boxH) / 2, boxW, boxH)
}

function world2px(xCm: number, yCm: number, w: number, h: number): [number, number] {
  return [(xCm + PITCH_W_CM / 2) / PITCH_W_CM * w,
          (yCm + PITCH_H_CM / 2) / PITCH_H_CM * h]
}

function drawFrame(canvas: HTMLCanvasElement, data: FrameRow | undefined): string {
  const ctx = canvas.getContext('2d')
  if (!ctx) return '—'
  const { width: w, height: h } = canvas
  drawField(ctx, w, h)
  if (!data || !data.players) {
    ctx.fillStyle = 'rgba(255,255,255,0.5)'
    ctx.font = '12px sans-serif'
    ctx.fillText('no data', 10, 18)
    return '—'
  }
  for (const p of data.players) {
    const [px, py] = world2px(p.x, p.y, w, h)
    ctx.fillStyle = p.team === 0 ? '#fbbf24' : p.team === 1 ? '#60a5fa' : '#bbb'
    ctx.strokeStyle = 'rgba(0,0,0,0.6)'
    ctx.lineWidth = 1
    ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
    ctx.fillStyle = '#1f2328'
    ctx.font = '8px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText(String(p.jersey ?? '?'), px, py + 3)
  }
  if (data.ball) {
    const [bx, by] = world2px(data.ball.x, data.ball.y, w, h)
    ctx.fillStyle = '#fff'
    ctx.strokeStyle = '#000'
    ctx.beginPath(); ctx.arc(bx, by, 3, 0, Math.PI * 2); ctx.fill(); ctx.stroke()
  }
  const maxS = Math.max(0, ...data.players.map((p) => p.speed || 0)).toFixed(1)
  return `${data.players.length} players, max speed ${maxS} m/s`
}

/** Keyed wrapper: the caches/refs below are sized for ONE game and ONE
 *  variant list at mount (the Jinja page got this for free from full page
 *  loads). Remounting on any change keeps game A's frames from rendering
 *  under game B's title. */
export default function TrackingCompare() {
  const { gameCode = '' } = useParams<{ gameCode: string }>()
  const [params] = useSearchParams()
  const variantsRaw = params.get('variants') ?? ',smoothed_v1,smoothed_v2'
  return <TrackingCompareInner key={`${gameCode}::${variantsRaw}`}
                               gameCode={gameCode} variantsRaw={variantsRaw} />
}

function TrackingCompareInner({ gameCode, variantsRaw }:
                              { gameCode: string; variantsRaw: string }) {
  const variants = variantsRaw.split(',').map((v) => v.trim())

  const [facts, setFacts] = useState<GameFacts | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [frame, setFrame] = useState(0)
  const [activeIdx, setActiveIdx] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [stats, setStats] = useState<string[]>(variants.map(() => '—'))

  const canvases = useRef<(HTMLCanvasElement | null)[]>([])
  const caches = useRef(variants.map(() => new Map<number, FrameRow>()))
  const fetching = useRef(variants.map(() => false))
  const paneFrame = useRef(variants.map(() => 0))
  const playRef = useRef(false)
  const hadFetchError = useRef(false)
  const stateRef = useRef({ frame: 0, activeIdx: 0 })
  stateRef.current = { frame, activeIdx }

  useEffect(() => {
    api<{ games: Array<{ game_code: string; total_frames: number | null;
                         fps: number | null; organization_id: number;
                         org_code: string }> }>(
      `/api/v1/qa/games?game_code=${pathSeg(gameCode)}`)
      .then((d) => {
        const g = d.games.find((x) => x.game_code === gameCode)
        if (!g || !g.total_frames || !g.fps) {
          setError(`game ${gameCode} not found or missing frames/fps`)
          return
        }
        setFacts({ total_frames: g.total_frames, fps: g.fps,
                   org_id: g.organization_id, org_code: g.org_code })
      })
      .catch((e: Error) => setError(e.message))
  }, [gameCode])

  async function fetchRange(variantIdx: number, startFrame: number, count: number) {
    if (fetching.current[variantIdx] || !facts) return
    fetching.current[variantIdx] = true
    const v = variants[variantIdx]
    const url = `${API_BASE}/api/v1/workbench/frames/${startFrame}/${count}` +
      `?game=${pathSeg(gameCode)}&org=${facts.org_id}` +
      (v ? `&version=${encodeURIComponent(v)}` : '')
    try {
      const r = await fetch(url, { credentials: 'include' })
      if (!r.ok) { hadFetchError.current = true; return }
      const data: FrameRow[] = await r.json()
      if (Array.isArray(data)) {
        for (const row of data) {
          if (row && row.frame !== undefined) caches.current[variantIdx]!.set(row.frame, row)
        }
      }
    } catch (e) {
      console.warn(`frames ${variantIdx} ${startFrame}:${count}`, e)
      hadFetchError.current = true
    } finally {
      fetching.current[variantIdx] = false
    }
  }

  async function ensureCached(f: number, activeOnly: boolean) {
    if (!facts) return
    const a = Math.max(0, f - PREFETCH_RADIUS)
    const b = Math.min(facts.total_frames - 1, f + PREFETCH_RADIUS)
    const idxs = activeOnly ? [stateRef.current.activeIdx] : variants.map((_, i) => i)
    const tasks: Promise<void>[] = []
    for (const v of idxs) {
      if (fetching.current[v]) continue
      let i = caches.current[v]!.has(f) ? a : f
      while (i <= b && caches.current[v]!.has(i)) i++
      if (i > b) continue
      const end = Math.min(i + BATCH_SIZE - 1, b)
      tasks.push(fetchRange(v, i, end - i + 1))
    }
    await Promise.all(tasks)
  }

  function draw() {
    const next = variants.map((_, i) => {
      const canvas = canvases.current[i]
      return canvas ? drawFrame(canvas, caches.current[i]!.get(paneFrame.current[i]!)) : '—'
    })
    setStats(next)
    // A failed fetch plus every pane empty is the "three silent green
    // rectangles" state — say so. An empty first paint alone is not an
    // error (the seed batch may still be loading).
    if (hadFetchError.current && next.every((st) => st === '—')
        && caches.current.every((c) => c.size === 0)) {
      setError('Frame requests are failing — check the game code and your org access.')
    }
  }

  async function render(f: number) {
    if (!facts) return
    setFrame(f)
    stateRef.current.frame = f
    if (playRef.current) {
      paneFrame.current[stateRef.current.activeIdx] = f
      if (!caches.current[stateRef.current.activeIdx]!.has(f)) await ensureCached(f, true)
      const ahead = Math.min(facts.total_frames - 1, f + REFETCH_TRIGGER)
      if (!caches.current[stateRef.current.activeIdx]!.has(ahead)) {
        ensureCached(f, true).catch(() => {})
      }
    } else {
      variants.forEach((_, i) => { paneFrame.current[i] = f })
      if (variants.some((_, i) => !caches.current[i]!.has(f))) await ensureCached(f, false)
      const ahead = Math.min(facts.total_frames - 1, f + REFETCH_TRIGGER)
      if (!variants.every((_, i) => caches.current[i]!.has(ahead))) {
        ensureCached(f, false).catch(() => {})
      }
    }
    draw()
  }

  function step(delta: number) {
    if (!facts) return
    render(Math.max(0, Math.min(facts.total_frames - 1, stateRef.current.frame + delta)))
  }

  async function playLoop() {
    // Recursive timeout: the next tick schedules only after this render is
    // done, so cache misses pause playback instead of piling up fetches.
    if (!playRef.current || !facts) return
    const f = stateRef.current.frame + 1
    if (f >= facts.total_frames) { togglePlay(); return }
    const tickStart = performance.now()
    await render(f)
    const delay = Math.max(0, 1000 / facts.fps - (performance.now() - tickStart))
    setTimeout(playLoop, delay)
  }

  function togglePlay() {
    playRef.current = !playRef.current
    setPlaying(playRef.current)
    if (playRef.current) playLoop()
  }

  // Seed render once the facts arrive.
  useEffect(() => {
    if (!facts) return
    render(Math.min(SEED_FRAME, facts.total_frames - 1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facts])

  // The play loop re-arms itself with setTimeout; navigation must stop
  // it or it fetches frames for a dead tree until the game ends.
  useEffect(() => () => { playRef.current = false }, [])

  // Keyboard: arrows step (shift = 30), space toggles play.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowLeft') { step(e.shiftKey ? -30 : -1); e.preventDefault() }
      else if (e.key === 'ArrowRight') { step(e.shiftKey ? 30 : 1); e.preventDefault() }
      else if (e.key === ' ') { togglePlay(); e.preventDefault() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facts])

  if (error) return <div className="tc-container"><p className="error">{error}</p></div>
  if (!facts) return <div className="tc-container"><p className="muted">Loading…</p></div>

  return (
    <div className="tc-container">
      <div className="tc-header">
        <h1>{gameCode} — Tracking Compare</h1>
        <span className="muted tc-facts">
          org={facts.org_code} · fps={facts.fps} · total_frames={facts.total_frames}
        </span>
      </div>

      <div className="tc-controls">
        <button onClick={() => step(-30)}>«30</button>
        <button onClick={() => step(-1)}>‹</button>
        <input type="range" min={0} max={facts.total_frames - 1} step={1}
               value={frame} onChange={(e) => render(parseInt(e.target.value, 10))} />
        <button onClick={() => step(1)}>›</button>
        <button onClick={() => step(30)}>30»</button>
        <span className="frame-display">
          frame {frame} ({(frame / facts.fps).toFixed(1)}s)
        </span>
        <label className="muted tc-active-label">play:{' '}
          <select value={activeIdx}
                  onChange={(e) => setActiveIdx(parseInt(e.target.value, 10))}>
            {variants.map((v, i) => <option key={i} value={i}>{v || 'raw'}</option>)}
          </select>
        </label>
        <button onClick={togglePlay}>{playing ? '❚❚ pause' : '▶ play active'}</button>
      </div>

      <div className="tc-grid"
           style={{ gridTemplateColumns: `repeat(${variants.length}, 1fr)` }}>
        {variants.map((v, i) => (
          <div key={i} className={`tc-pane ${i === activeIdx ? 'active' : ''}`}>
            <div className="tc-pane-title">
              <span className={`v-tag ${v ? '' : 'raw'}`}>{v || 'raw'}</span>
            </div>
            <div className="tc-pane-body" onClick={() => setActiveIdx(i)}>
              <canvas ref={(el) => { canvases.current[i] = el }} width={420} height={280} />
            </div>
            <div className="pane-stats">{stats[i]}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
